/**
 * Page Capabilities Management Component
 *
 * Admin interface for managing page-level capabilities for an organization.
 * Allows administrators to enable/disable specific pages and configure
 * display behavior for disabled pages.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Settings,
  Check,
  X,
  Eye,
  EyeOff,
  AlertCircle,
  Search,
  Filter,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  Info,
  Lock,
  Unlock,
  RotateCcw,
} from 'lucide-react';
import {
  usePageCapabilitiesList,
  usePageCapabilityUpdate,
  clearOrgPageCapabilityCache,
} from '@/hooks/usePageCapability';
import {
  PageCapabilityWithOrgStatus,
  PageCategory,
  PAGE_CATEGORY_LABELS,
  DisplayBehavior,
  groupPagesByCategory,
} from '@/types/pageCapabilities';
import { useAppTranslation } from '@/contexts/TranslationContext';

// ============================================================================
// TYPES
// ============================================================================

interface PageCapabilitiesManagementProps {
  organizationId: number;
  organizationName: string;
  onClose?: () => void;
}

// ============================================================================
// COMPONENT
// ============================================================================

export function PageCapabilitiesManagement({
  organizationId,
  organizationName,
  onClose,
}: PageCapabilitiesManagementProps) {
  const { t, locale } = useAppTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<PageCategory | 'all'>('all');
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({});

  // Load page capabilities
  const { pages, pagesByCategory, loading, error, refetch, patchPage } = usePageCapabilitiesList(
    organizationId,
    selectedCategory === 'all' ? undefined : selectedCategory
  );

  // Update hooks
  const {
    updatePageCapability,
    togglePageCapability,
    resetPageCapability,
    saving,
    error: updateError,
  } = usePageCapabilityUpdate(organizationId);

  // Initialize expanded state for all categories
  useEffect(() => {
    if (Object.keys(pagesByCategory).length > 0 && Object.keys(expandedCategories).length === 0) {
      const initial: Record<string, boolean> = {};
      Object.keys(pagesByCategory).forEach(cat => {
        initial[cat] = true; // Expand all by default
      });
      setExpandedCategories(initial);
    }
  }, [pagesByCategory]);

  // Filter pages by search query
  const filteredPagesByCategory = useMemo(() => {
    if (!searchQuery.trim()) return pagesByCategory;

    const query = searchQuery.toLowerCase();
    const filtered: Record<string, PageCapabilityWithOrgStatus[]> = {};

    Object.entries(pagesByCategory).forEach(([category, categoryPages]) => {
      const matchingPages = categoryPages.filter(
        page =>
          page.page_name.toLowerCase().includes(query) ||
          page.page_name_es?.toLowerCase().includes(query) ||
          page.page_key.toLowerCase().includes(query) ||
          page.description?.toLowerCase().includes(query)
      );
      if (matchingPages.length > 0) {
        filtered[category] = matchingPages;
      }
    });

    return filtered;
  }, [pagesByCategory, searchQuery]);

  // Get unique categories from pages
  const availableCategories = useMemo(() => {
    return Object.keys(pagesByCategory) as PageCategory[];
  }, [pagesByCategory]);

  // Stats
  const stats = useMemo(() => {
    let total = 0;
    let enabled = 0;
    let disabled = 0;
    let customized = 0;

    pages.forEach(page => {
      total++;
      if (page.effective_enabled) enabled++;
      else disabled++;
      if (page.org_enabled !== null) customized++;
    });

    return { total, enabled, disabled, customized };
  }, [pages]);

  // Toggle category expansion
  const toggleCategory = (category: string) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  // Handle toggle page capability.
  // Optimistic local patch + silent reconcile so the list re-renders in place
  // instead of unmounting (which reset scroll to the top). Only a failure
  // triggers a full, spinner-showing refetch to restore server truth.
  const handleToggle = async (page: PageCapabilityWithOrgStatus) => {
    const next = !page.effective_enabled;
    patchPage(page.page_key, { effective_enabled: next, org_enabled: next });
    const success = await togglePageCapability(page.page_key, page.effective_enabled);
    if (success) {
      refetch(true);
    } else {
      refetch();
    }
  };

  // Handle update display behavior
  const handleUpdateBehavior = async (page: PageCapabilityWithOrgStatus, behavior: DisplayBehavior) => {
    patchPage(page.page_key, { org_config: { ...(page.org_config || {}), display_behavior: behavior } as any });
    const success = await updatePageCapability(
      page.page_key,
      page.effective_enabled,
      page.org_config,
      behavior
    );
    if (success) {
      refetch(true);
    } else {
      refetch();
    }
  };

  // Handle reset to default
  const handleReset = async (pageKey: string) => {
    const success = await resetPageCapability(pageKey);
    if (success) {
      refetch(true);
    } else {
      refetch();
    }
  };

  // Get category label
  const getCategoryLabel = (category: PageCategory) => {
    const labels = PAGE_CATEGORY_LABELS[category];
    return locale === 'es' ? labels?.es : labels?.en || category;
  };

  // Get page display name
  const getPageName = (page: PageCapabilityWithOrgStatus) => {
    return locale === 'es' && page.page_name_es ? page.page_name_es : page.page_name;
  };

  // Loading state
  if (loading) {
    return (
      <div className="page-capabilities-management bg-white dark:bg-gray-800 rounded-lg shadow">
        <div className="flex items-center justify-center p-12">
          <div className="flex flex-col items-center gap-4">
            <div className="w-10 h-10 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" />
            <p className="text-gray-500 dark:text-gray-400">
              {t('admin.loadingPageCapabilities', 'Loading page capabilities...')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="page-capabilities-management bg-white dark:bg-gray-800 rounded-lg shadow">
        <div className="flex flex-col items-center justify-center p-12 gap-4">
          <AlertCircle className="w-12 h-12 text-red-500" />
          <p className="text-red-600 dark:text-red-400">{error}</p>
          <button
            onClick={refetch}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            {t('common.retry', 'Retry')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="page-capabilities-management bg-white dark:bg-gray-800 rounded-lg shadow">
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg flex items-center justify-center">
            <Settings className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              {t('admin.pageAccessManagement', 'Page Access Management')}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">{organizationName}</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              clearOrgPageCapabilityCache(organizationId);
              refetch();
            }}
            className="p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            title={t('common.refresh', 'Refresh')}
          >
            <RefreshCw className="w-5 h-5" />
          </button>
          {onClose && (
            <button
              onClick={onClose}
              className="p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      {/* Stats Bar */}
      <div className="grid grid-cols-4 gap-4 p-4 bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
        <div className="text-center">
          <div className="text-2xl font-bold text-gray-900 dark:text-white">{stats.total}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            {t('admin.totalPages', 'Total Pages')}
          </div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-green-600">{stats.enabled}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            {t('admin.enabled', 'Enabled')}
          </div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-red-600">{stats.disabled}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            {t('admin.disabled', 'Disabled')}
          </div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-indigo-600">{stats.customized}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            {t('admin.customized', 'Customized')}
          </div>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row gap-4 p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder={t('admin.searchPages', 'Search pages...')}
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          />
        </div>

        <div className="flex items-center gap-2">
          <Filter className="w-5 h-5 text-gray-400" />
          <select
            value={selectedCategory}
            onChange={e => setSelectedCategory(e.target.value as PageCategory | 'all')}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          >
            <option value="all">{t('admin.allCategories', 'All Categories')}</option>
            {availableCategories.map(cat => (
              <option key={cat} value={cat}>
                {getCategoryLabel(cat)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Error message */}
      {updateError && (
        <div className="mx-4 mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-2 text-red-700 dark:text-red-400">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span>{updateError}</span>
        </div>
      )}

      {/* Page List by Category */}
      <div className="p-4 space-y-4 max-h-[600px] overflow-y-auto">
        {Object.entries(filteredPagesByCategory).map(([category, categoryPages]) => (
          <div key={category} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
            {/* Category Header */}
            <button
              onClick={() => toggleCategory(category)}
              className="w-full flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-900/50 hover:bg-gray-100 dark:hover:bg-gray-900/70 transition-colors"
            >
              <div className="flex items-center gap-3">
                {expandedCategories[category] ? (
                  <ChevronDown className="w-5 h-5 text-gray-500" />
                ) : (
                  <ChevronRight className="w-5 h-5 text-gray-500" />
                )}
                <span className="font-medium text-gray-900 dark:text-white">
                  {getCategoryLabel(category as PageCategory)}
                </span>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  ({categoryPages.length} {t('admin.pages', 'pages')})
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-green-600">
                  {categoryPages.filter(p => p.effective_enabled).length} {t('admin.enabled', 'enabled')}
                </span>
                <span className="text-gray-300 dark:text-gray-600">|</span>
                <span className="text-sm text-red-600">
                  {categoryPages.filter(p => !p.effective_enabled).length} {t('admin.disabled', 'disabled')}
                </span>
              </div>
            </button>

            {/* Category Pages */}
            {expandedCategories[category] && (
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {categoryPages.map(page => (
                  <div
                    key={page.page_key}
                    className={`p-4 ${
                      page.effective_enabled
                        ? 'bg-green-50/50 dark:bg-green-900/10'
                        : 'bg-gray-50 dark:bg-gray-900/30'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      {/* Page Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-gray-900 dark:text-white">
                            {getPageName(page)}
                          </span>
                          {page.org_enabled !== null && (
                            <span className="px-2 py-0.5 text-xs bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded">
                              {t('admin.customized', 'Customized')}
                            </span>
                          )}
                          {!page.default_enabled && page.org_enabled === null && (
                            <span className="px-2 py-0.5 text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded">
                              {t('admin.defaultOff', 'Default Off')}
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">
                          {page.page_path}
                        </div>
                        {page.description && (
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {locale === 'es' && page.description_es ? page.description_es : page.description}
                          </p>
                        )}

                        {/* Display behavior info */}
                        {!page.effective_enabled && (
                          <div className="mt-2 flex items-center gap-2 text-sm">
                            <Info className="w-4 h-4 text-amber-500" />
                            <span className="text-amber-700 dark:text-amber-400">
                              {page.org_display_behavior === 'disable'
                                ? t('admin.behaviorDisable', 'Shown with lock overlay')
                                : page.org_display_behavior === 'show-upgrade'
                                ? t('admin.behaviorUpgrade', 'Shows upgrade prompt')
                                : t('admin.behaviorHide', 'Hidden from navigation')}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {/* Display behavior dropdown (only for disabled pages) */}
                        {!page.effective_enabled && (
                          <select
                            value={page.org_display_behavior || 'hide'}
                            onChange={e => handleUpdateBehavior(page, e.target.value as DisplayBehavior)}
                            disabled={saving === page.page_key}
                            className="text-sm px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                          >
                            <option value="hide">{t('admin.hide', 'Hide')}</option>
                            <option value="disable">{t('admin.showLocked', 'Show Locked')}</option>
                            <option value="show-upgrade">{t('admin.showUpgrade', 'Show Upgrade')}</option>
                          </select>
                        )}

                        {/* Reset button (only if customized) */}
                        {page.org_enabled !== null && (
                          <button
                            onClick={() => handleReset(page.page_key)}
                            disabled={saving === page.page_key}
                            className="p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                            title={t('admin.resetToDefault', 'Reset to default')}
                          >
                            <RotateCcw className="w-4 h-4" />
                          </button>
                        )}

                        {/* Toggle button — or a locked hint when the parent feature
                            capability is disabled (toggling would silently revert
                            because the page is gated by its parent capability). */}
                        {page.parent_capability && !page.parent_capability_enabled ? (
                          <span
                            className="flex items-center gap-2 px-3 py-2 rounded-lg font-medium text-sm bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-800/50 cursor-help"
                            title={t(
                              'admin.requiresFeatureCapability',
                              'Enable the "{{capability}}" feature (Features tab) before this page can be turned on.',
                              { capability: page.parent_capability }
                            )}
                          >
                            <Lock className="w-4 h-4" />
                            <span>{t('admin.requiresFeature', 'Requires feature')}</span>
                          </span>
                        ) : (
                          <button
                            onClick={() => handleToggle(page)}
                            disabled={saving === page.page_key}
                            className={`flex items-center gap-2 px-3 py-2 rounded-lg font-medium text-sm transition-colors ${
                              saving === page.page_key
                                ? 'bg-gray-200 dark:bg-gray-700 text-gray-500 cursor-not-allowed'
                                : page.effective_enabled
                                ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/50'
                                : 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 hover:bg-green-200 dark:hover:bg-green-900/50'
                            }`}
                          >
                            {saving === page.page_key ? (
                              <>
                                <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                                <span>{t('common.saving', 'Saving...')}</span>
                              </>
                            ) : page.effective_enabled ? (
                              <>
                                <EyeOff className="w-4 h-4" />
                                <span>{t('admin.disable', 'Disable')}</span>
                              </>
                            ) : (
                              <>
                                <Eye className="w-4 h-4" />
                                <span>{t('admin.enable', 'Enable')}</span>
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}

        {/* Empty state */}
        {Object.keys(filteredPagesByCategory).length === 0 && (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            {searchQuery
              ? t('admin.noMatchingPages', 'No pages match your search.')
              : t('admin.noPagesFound', 'No pages found.')}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
        <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
          <div className="flex items-center gap-2">
            <Info className="w-4 h-4" />
            <span>
              {t(
                'admin.pageCapabilitiesInfo',
                'Changes take effect immediately. Users will see updated navigation on their next page load.'
              )}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PageCapabilitiesManagement;
